import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { InboxProvider } from "./context/InboxContext";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ProductsPage from "./pages/ProductsPage";
import DDTListPage from "./pages/DDTListPage";
import DDTCreatePage from "./pages/DDTCreatePage";
import DDTViewPage from "./pages/DDTViewPage";
import DDTEditPage from "./pages/DDTEditPage";
import SettingsPage from "./pages/SettingsPage";
import AdminPage from "./pages/AdminPage";
import StatementPage from "./pages/StatementPage";
import PaymentsPage from "./pages/PaymentsPage";
import TrashPage from "./pages/TrashPage";
import QuotesListPage from "./pages/QuotesListPage";
import QuoteFormPage from "./pages/QuoteFormPage";
import SuppliersPage from "./pages/SuppliersPage";
import PurchasesListPage from "./pages/PurchasesListPage";
import PurchaseFormPage from "./pages/PurchaseFormPage";
import ExpensesPage from "./pages/ExpensesPage";
import IncomesPage from "./pages/IncomesPage";
import InboxPage from "./pages/InboxPage";
import InvoicesListPage from "./pages/InvoicesListPage";
import InvoiceFormPage from "./pages/InvoiceFormPage";
import EinvoiceGate from "./components/EinvoiceGate";

// Layout
import Layout from "./components/Layout";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-muted-foreground">Caricamento...</div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, superAdminOnly = false, companyOnly = false, masterOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (masterOnly && user.is_slave) return <Navigate to="/" replace />;

  // Super admin guard
  if (superAdminOnly && user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  // Company route guard (super_admin without company_id should not see company pages)
  if (companyOnly && user.role === 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (user) {
    return <Navigate to={user.role === 'super_admin' ? '/admin' : '/'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Super Admin Route */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute superAdminOnly>
            <AdminPage />
          </ProtectedRoute>
        }
      />

      {/* Company Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute companyOnly>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute companyOnly>
            <ClientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute companyOnly>
            <ProductsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ddt"
        element={
          <ProtectedRoute companyOnly>
            <DDTListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ddt/new"
        element={
          <ProtectedRoute companyOnly>
            <DDTCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ddt/:id"
        element={
          <ProtectedRoute companyOnly>
            <DDTViewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ddt/:id/edit"
        element={
          <ProtectedRoute companyOnly>
            <DDTEditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/statement"
        element={
          <ProtectedRoute companyOnly>
            <StatementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/movimenti"
        element={
          <ProtectedRoute companyOnly>
            <PaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/uscite"
        element={
          <ProtectedRoute companyOnly>
            <ExpensesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/entrate"
        element={
          <ProtectedRoute companyOnly>
            <IncomesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute companyOnly>
            <InboxPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cestino"
        element={
          <ProtectedRoute companyOnly>
            <TrashPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fatture"
        element={<ProtectedRoute companyOnly masterOnly><EinvoiceGate><InvoicesListPage /></EinvoiceGate></ProtectedRoute>}
      />
      <Route
        path="/fatture/new"
        element={<ProtectedRoute companyOnly masterOnly><EinvoiceGate><InvoiceFormPage /></EinvoiceGate></ProtectedRoute>}
      />
      <Route
        path="/fatture/:id"
        element={<ProtectedRoute companyOnly masterOnly><EinvoiceGate><InvoiceFormPage /></EinvoiceGate></ProtectedRoute>}
      />
      <Route
        path="/preventivi"
        element={
          <ProtectedRoute companyOnly>
            <QuotesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preventivi/new"
        element={
          <ProtectedRoute companyOnly>
            <QuoteFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preventivi/:id/edit"
        element={
          <ProtectedRoute companyOnly>
            <QuoteFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fornitori"
        element={
          <ProtectedRoute companyOnly>
            <SuppliersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisti"
        element={
          <ProtectedRoute companyOnly>
            <PurchasesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisti/new"
        element={
          <ProtectedRoute companyOnly>
            <PurchaseFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisti/:id/edit"
        element={
          <ProtectedRoute companyOnly>
            <PurchaseFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute companyOnly>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect based on role */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <InboxProvider>
          <AppRoutes />
          <Toaster position="top-center" />
        </InboxProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
